/**
 * ConfidenceIndicator — Sprint 23 F006
 *
 * Renders a coloured dot indicating extraction confidence level:
 *   - Green  (>= 0.90): High confidence — exact or near-exact match
 *   - Amber  (0.60–0.89): Medium confidence — fuzzy match, review recommended
 *   - Red    (< 0.60):  Low confidence — manual review required
 *   - None   (undefined): Unmapped item — no indicator
 */

interface ConfidenceIndicatorProps {
  /** Confidence value in range 0–1, or undefined for unmapped items */
  confidence?: number;
  /** Optional CSS class name for the wrapping span */
  className?: string;
}

export function ConfidenceIndicator({ confidence, className }: ConfidenceIndicatorProps) {
  if (confidence === undefined || confidence === null) {
    return (
      <span
        data-testid="confidence-indicator"
        className={className}
        aria-label="Not mapped"
        title="Not mapped"
      >
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
      </span>
    );
  }

  if (confidence >= 0.90) {
    return (
      <span
        data-testid="confidence-indicator"
        className={className}
        aria-label={`High confidence: ${Math.round(confidence * 100)}%`}
        title={`High confidence: ${Math.round(confidence * 100)}%`}
      >
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
      </span>
    );
  }

  if (confidence >= 0.60) {
    return (
      <span
        data-testid="confidence-indicator"
        className={className}
        aria-label={`Medium confidence: ${Math.round(confidence * 100)}%`}
        title={`Medium confidence: ${Math.round(confidence * 100)}%`}
      >
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" />
      </span>
    );
  }

  return (
    <span
      data-testid="confidence-indicator"
      className={className}
      aria-label={`Low confidence: ${Math.round(confidence * 100)}%`}
      title={`Low confidence: ${Math.round(confidence * 100)}%`}
    >
      <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
    </span>
  );
}
